import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NavItem } from '../types';
import { useAnimationCache } from '../hooks/useAnimationCache';
import '../styles/Header.css';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Initialize animation caching for header animations
  const { 
    registerElement, 
    animateElement, 
    cacheElementState, 
    applyCachedState,
    enabled: animationsEnabled 
  } = useAnimationCache({
    performanceMode: 'high',
    autoPromoteToRenderLayer: true
  });

  const navItems: NavItem[] = [
    { id: 'home', label: 'Home', href: '/' },
    { id: 'projects', label: 'Projects', href: '/projects' },
    { id: 'articles', label: 'Articles', href: '/articles' },
    { id: 'store', label: 'Store', href: '/store' },
    { id: 'about', label: 'About', href: '/about' },
    { id: 'docs', label: 'Docs', href: '/docs' },
    { id: 'contact', label: 'Contact', href: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      
      // Use cached animation states for performance
      if (animationsEnabled) {
        const headerElement = document.querySelector('.header') as HTMLElement;
        if (headerElement) {
          registerElement('main-header', headerElement);
          
          if (scrolled) {
            // Cache the scrolled state
            cacheElementState('header-scrolled');
            headerElement.style.transform = 'translateY(0)';
            headerElement.style.boxShadow = '0 2px 20px rgba(0, 0, 0, 0.1)';
          } else {
            // Apply cached normal state
            applyCachedState('header-normal', 50);
            headerElement.style.boxShadow = 'none';
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [animationsEnabled, registerElement, cacheElementState, applyCachedState]);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when menu is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    const newState = !isMenuOpen;
    setIsMenuOpen(newState);
    
    // Enhance menu animations with caching
    if (animationsEnabled) {
      const navElement = document.querySelector('.header__nav') as HTMLElement;
      const overlayElement = document.querySelector('.header__overlay') as HTMLElement;
      
      if (navElement) {
        registerElement('mobile-nav', navElement);
        
        if (newState) {
          // Opening menu - use cached slide-in animation
          animateElement('mobile-nav', 'fadeInTransform', {
            duration: 300,
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)'
          });
        } else {
          // Closing menu - apply reverse animation
          navElement.style.transform = 'translateX(100%)';
          navElement.style.transition = 'transform 0.3s ease-out';
          setTimeout(() => {
            navElement.style.transform = '';
            navElement.style.transition = '';
          }, 300);
        }
      }
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (href: string) => {
    closeMenu();
    
    // Handle anchor links within the same page
    if (href.startsWith('/#') && location.pathname === '/') {
      const targetId = href.substring(2);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (href.startsWith('/#')) {
      // Navigate to home first, then scroll to section
      setTimeout(() => {
        const targetId = href.substring(2);
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const getCurrentPageLabel = () => {
    const currentItem = navItems.find(item => item.href === location.pathname);
    return currentItem ? currentItem.label : 'Page';
  };

  return (
    <header 
      className={`header ${isScrolled ? 'header--scrolled' : ''}`}
      role="banner"
      aria-label="Site header"
    >
      <div className="container">
        <div className="header__content">
          {/* Logo/Brand with semantic markup */}
          <div className="header__logo">
            <Link 
              to="/" 
              onClick={closeMenu}
              aria-label="Griffin's World - Go to homepage"
              className="header__logo-link"
            >
              <span className="header__logo-text" aria-hidden="true">Griffin's World</span>
            </Link>
          </div>

          {/* Main Navigation with semantic HTML5 nav element */}
          <nav 
            className={`header__nav ${isMenuOpen ? 'header__nav--open' : ''}`}
            role="navigation"
            aria-label="Main navigation"
            aria-expanded={isMenuOpen}
          >
            <ul 
              className="header__nav-list"
              role="menubar"
              aria-label="Main menu"
            >
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <li 
                    key={item.id} 
                    className="header__nav-item"
                    role="none"
                  >
                    {item.href.startsWith('/#') ? (
                      <a
                        href={item.href}
                        className={`header__nav-link ${isActive ? 'header__nav-link--active' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavClick(item.href);
                        }}
                        role="menuitem"
                        aria-label={`Navigate to ${item.label} section`}
                        aria-current={isActive ? 'page' : undefined}
                        tabIndex={isMenuOpen ? 0 : -1}
                      >
                        <span>{item.label}</span>
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className={`header__nav-link ${isActive ? 'header__nav-link--active' : ''}`}
                        onClick={closeMenu}
                        role="menuitem"
                        aria-label={`Navigate to ${item.label} page`}
                        aria-current={isActive ? 'page' : undefined}
                        tabIndex={isMenuOpen ? 0 : -1}
                      >
                        <span>{item.label}</span>
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Mobile Menu Toggle with enhanced accessibility */}
          <button
            className={`header__menu-toggle ${isMenuOpen ? 'header__menu-toggle--open' : ''}`}
            onClick={toggleMenu}
            aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMenuOpen}
            aria-controls="header-nav"
            aria-haspopup="true"
            type="button"
          >
            <span className="header__menu-line" aria-hidden="true"></span>
            <span className="header__menu-line" aria-hidden="true"></span>
            <span className="header__menu-line" aria-hidden="true"></span>
            <span className="visually-hidden">
              {isMenuOpen ? 'Close' : 'Open'} navigation menu
            </span>
          </button>

          {/* Screen Reader Navigation Status */}
          <div 
            className="visually-hidden" 
            aria-live="polite" 
            aria-atomic="true"
          >
            {isMenuOpen ? `Navigation menu opened. Currently on ${getCurrentPageLabel()} page.` : ''}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="header__overlay"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}
    </header>
  );
};

export default Header; 